/**
 * WCO-3277 — Admin data-repair UI (preview / apply / rollback).
 */
import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { AlertTriangle, Database, History, Play, RotateCcw, Search } from 'lucide-react';
import { useLanguage } from '../../i18n/LanguageContext';
import operatorService, {
  DataRepairActionMeta,
  DataRepairApplyResult,
  DataRepairHistoryEntry,
  DataRepairPreviewResult,
} from '../../services/operatorService';
import { DATA_REPAIR_UI_MAX_DISPLAY_ROWS } from './dataRepairLimits';

const DataRepairPanel: React.FC = () => {
  const { t } = useLanguage();
  const [selectedAction, setSelectedAction] = useState<string>('');
  const [inputJson, setInputJson] = useState<string>('{}');
  const [preview, setPreview] = useState<DataRepairPreviewResult | null>(null);
  const [previewInput, setPreviewInput] = useState<Record<string, unknown> | null>(null);
  const [previewFingerprint, setPreviewFingerprint] = useState<string | null>(null);
  const [lastApplyResult, setLastApplyResult] = useState<DataRepairApplyResult | null>(null);
  const [lastApplyActionId, setLastApplyActionId] = useState<string | null>(null);
  const [showApplyConfirm, setShowApplyConfirm] = useState(false);
  const [loading, setLoading] = useState<'preview' | 'apply' | 'rollback' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const {
    data: actionsData,
    isLoading: actionsLoading,
    isError: actionsError,
    error: actionsQueryError,
  } = useQuery<DataRepairActionMeta[]>(
    ['data-repair-actions'],
    () => operatorService.listDataRepairActions(),
    { staleTime: 60000, retry: false },
  );
  const actions = Array.isArray(actionsData) ? actionsData : [];

  const selectedMeta = actions.find((a) => a.id === selectedAction);

  // Recent repairs, so a repairLogId survives a page refresh and stays rollbackable.
  const { data: historyData, refetch: refetchHistory } = useQuery<DataRepairHistoryEntry[]>(
    ['data-repair-history'],
    () => operatorService.listDataRepairHistory(),
    { staleTime: 0, retry: false },
  );
  const history = Array.isArray(historyData) ? historyData : [];
  /** Refresh the list after a repair; never let a failed refresh surface as an error. */
  const reloadHistory = () => { void refetchHistory?.(); };

  const resetPreviewState = () => {
    setPreview(null);
    setPreviewInput(null);
    setPreviewFingerprint(null);
  };

  const parseInput = (): Record<string, unknown> => {
    const trimmed = inputJson.trim();
    if (!trimmed) return {};
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('invalid');
      }
      return parsed as Record<string, unknown>;
    } catch {
      throw new Error(t('dataRepair.inputJsonInvalid'));
    }
  };

  const handlePreview = async () => {
    if (!selectedAction) return;
    setLoading('preview');
    setError(null);
    setSuccess(null);
    resetPreviewState();
    setLastApplyResult(null);
    setLastApplyActionId(null);
    try {
      const input = parseInput();
      const result = await operatorService.previewDataRepair(selectedAction, input);
      setPreview(result);
      setPreviewInput(input);
      setPreviewFingerprint(result.previewFingerprint ?? null);
    } catch (err: any) {
      const message = err.message === t('dataRepair.inputJsonInvalid')
        ? err.message
        : (err.response?.data?.message || err.response?.data?.error || err.message || t('dataRepair.previewError'));
      setError(message);
    } finally {
      setLoading(null);
    }
  };

  const handleApply = async () => {
    if (!selectedAction || !preview?.allGuardsPassed || !previewInput || !previewFingerprint) return;
    setLoading('apply');
    setError(null);
    setSuccess(null);
    try {
      const result = await operatorService.applyDataRepair(selectedAction, previewInput, previewFingerprint);
      setLastApplyResult(result);
      setLastApplyActionId(selectedAction);
      setSuccess(t('dataRepair.applySuccessWithId').replace('{{id}}', String(result.repairLogId)));
      setShowApplyConfirm(false);
      resetPreviewState();
      reloadHistory();
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || err.message || t('dataRepair.applyError'));
      setShowApplyConfirm(false);
    } finally {
      setLoading(null);
    }
  };

  /** Roll back a specific repair — the one just applied, or any row from the history list. */
  const rollbackById = async (actionIdForRollback: string, repairLogId: number) => {
    setLoading('rollback');
    setError(null);
    setSuccess(null);
    try {
      await operatorService.rollbackDataRepair(actionIdForRollback, repairLogId);
      setSuccess(t('dataRepair.rollbackSuccess'));
      setLastApplyResult(null);
      setLastApplyActionId(null);
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || err.message || t('dataRepair.rollbackError'));
    } finally {
      setLoading(null);
      reloadHistory();
    }
  };

  const handleRollback = async () => {
    if (!lastApplyResult?.repairLogId || !lastApplyActionId) return;
    await rollbackById(lastApplyActionId, lastApplyResult.repairLogId);
  };

  const formatPreviewCell = (key: string, val: unknown): string => {
    if (key === 'kind' && typeof val === 'string') {
      const labelKey = `dataRepair.previewKinds.${val}`;
      const label = t(labelKey);
      return label !== labelKey ? label : val;
    }
    return String(val ?? '');
  };

  // t() returns the key itself when a translation is missing, so compare against the key
  // to fall back to the server-provided description for actions without a locale entry.
  const actionLabel = (action: { id: string; description: string }): string => {
    const key = `dataRepair.actions.${action.id}.description`;
    const translated = t(key);
    return translated === key ? action.description : translated;
  };

  const renderStateTable = (title: string, rows: Record<string, unknown>[], translatePreviewKinds = false) => {
    const displayRows = rows.slice(0, DATA_REPAIR_UI_MAX_DISPLAY_ROWS);
    const truncated = rows.length > displayRows.length;
    // Rows of one repair can have different shapes (e.g. a summary row next to insert rows),
    // so the header is the union of every row's keys and each cell is looked up by key —
    // never by position, which shifts values under the wrong header.
    const columns = Array.from(new Set(displayRows.flatMap((row) => Object.keys(row))));
    return (
      <div className="space-y-2">
        <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{title}</h4>
        {rows.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('dataRepair.noRows')}</p>
        ) : (
          <>
            {truncated && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('dataRepair.rowsTruncated')
                  .replace('{{shown}}', String(displayRows.length))
                  .replace('{{total}}', String(rows.length))}
              </p>
            )}
            <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-md">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    {columns.map((key) => (
                      <th key={key} className="px-2 py-1 text-left font-medium text-gray-600 dark:text-gray-400">{key}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {displayRows.map((row, idx) => (
                    <tr key={idx} className="border-t border-gray-100 dark:border-gray-700">
                      {columns.map((key) => (
                        <td key={key} className="px-2 py-1 font-mono text-gray-700 dark:text-gray-300">
                          {translatePreviewKinds ? formatPreviewCell(key, row[key]) : String(row[key] ?? '')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    );
  };

  if (actionsLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-6 text-sm text-gray-500 dark:text-gray-400">
        {t('dataRepair.actionsLoading')}
      </div>
    );
  }

  if (actionsError) {
    const errMsg = (actionsQueryError as any)?.response?.data?.message
      || (actionsQueryError as Error)?.message
      || t('dataRepair.actionsLoadError');
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-red-200 dark:border-red-800 shadow-sm p-6">
        <p className="text-sm text-red-600 dark:text-red-400">{errMsg}</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <Database className="h-5 w-5 text-primary-600 dark:text-primary-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('dataRepair.title')}</h2>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('dataRepair.description')}</p>
        <p className="text-xs text-amber-700 dark:text-amber-400 mt-2">{t('dataRepair.prodAllowedNote')}</p>
      </div>

      <div className="p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('dataRepair.actionLabel')}
          </label>
          <select
            value={selectedAction}
            onChange={(e) => {
              setSelectedAction(e.target.value);
              resetPreviewState();
              setError(null);
              setSuccess(null);
              setInputJson('{}');
              setLastApplyResult(null);
              setLastApplyActionId(null);
            }}
            className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
          >
            <option value="">{t('dataRepair.selectAction')}</option>
            {actions.map((action) => (
              <option key={action.id} value={action.id}>
                {actionLabel(action)}
              </option>
            ))}
          </select>
          {actions.length === 0 && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('dataRepair.noActions')}</p>
          )}
        </div>

        {selectedAction && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('dataRepair.inputJson')}
            </label>
            {/* WCO-3277 review: tell the operator which keys this action accepts —
                a mistyped key is now rejected, so it must be discoverable. */}
            {selectedMeta && (
              selectedMeta.params && selectedMeta.params.length > 0 ? (
                <div className="mb-2 text-xs text-gray-600 dark:text-gray-400">
                  <p className="font-medium mb-1">{t('dataRepair.acceptedParams')}</p>
                  <ul className="space-y-0.5">
                    {selectedMeta.params.map((p) => (
                      <li key={p.name}>
                        <code className="font-mono text-gray-800 dark:text-gray-200">{p.name}</code>
                        {p.required && <span className="text-amber-600 dark:text-amber-400"> *</span>}
                        {' — '}{p.description}
                        {p.example !== undefined && (
                          <span className="text-gray-500 dark:text-gray-500">
                            {' '}({t('dataRepair.paramExample')}: <code className="font-mono">{JSON.stringify(p.example)}</code>)
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={() => {
                      const skeleton: Record<string, unknown> = {};
                      (selectedMeta.params || []).forEach((p) => {
                        if (p.example !== undefined) skeleton[p.name] = p.example;
                      });
                      setInputJson(JSON.stringify(skeleton, null, 2));
                      resetPreviewState();
                    }}
                    className="mt-1 underline text-primary-600 dark:text-primary-400"
                  >
                    {t('dataRepair.insertExample')}
                  </button>
                </div>
              ) : (
                <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">{t('dataRepair.noParams')}</p>
              )
            )}
            <textarea
              value={inputJson}
              onChange={(e) => {
                setInputJson(e.target.value);
                resetPreviewState();
              }}
              rows={4}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm font-mono"
              placeholder={t('dataRepair.inputJsonHint')}
            />
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <button
            onClick={handlePreview}
            disabled={!selectedAction || loading !== null}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white text-sm rounded-md"
          >
            <Search className="h-4 w-4" />
            {loading === 'preview' ? t('dataRepair.previewLoading') : t('dataRepair.previewButton')}
          </button>
          {preview && previewInput && previewFingerprint && (
            <button
              onClick={() => setShowApplyConfirm(true)}
              disabled={!preview.allGuardsPassed || loading !== null}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-sm rounded-md"
            >
              <Play className="h-4 w-4" />
              {t('dataRepair.applyButton')}
            </button>
          )}
          {lastApplyResult?.repairLogId && lastApplyActionId && (
            <button
              onClick={handleRollback}
              disabled={loading !== null}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-gray-700 hover:bg-gray-800 disabled:opacity-50 text-white text-sm rounded-md"
            >
              <RotateCcw className="h-4 w-4" />
              {loading === 'rollback' ? t('dataRepair.rollbackLoading') : t('dataRepair.rollbackButton')}
            </button>
          )}
        </div>

        {preview && (
          <div className="space-y-4 border-t border-gray-200 dark:border-gray-700 pt-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">{t('dataRepair.previewNote')}</p>
            <div className={`text-sm font-medium ${preview.allGuardsPassed ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {preview.allGuardsPassed ? t('dataRepair.guardsPassed') : t('dataRepair.guardsFailed')}
            </div>
            <ul className="text-xs space-y-1">
              {preview.guards.map((guard) => (
                <li key={guard.name} className={guard.passed ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                  {guard.name}: {guard.passed ? t('dataRepair.guardOk') : (guard.message || t('dataRepair.guardFailed'))}
                </li>
              ))}
            </ul>
            {renderStateTable(t('dataRepair.beforeState'), preview.before)}
            {/* WCO-3277 review: when guards fail the framework never simulates the repair and
                returns after === before. Rendering it as "After" reads as "ran, changed nothing". */}
            {preview.allGuardsPassed ? (
              <>
                {(() => {
                  const summary = preview.after.filter((r) => String(r.kind || '').startsWith('preview_'));
                  const stateRows = preview.after.filter(
                    (r) => !String(r.kind || '').startsWith('preview_') && r.kind !== 'rollback_edit_body',
                  );
                  return (
                    <>
                      {summary.length > 0 && renderStateTable(t('dataRepair.previewSummaryTitle'), summary, true)}
                      {renderStateTable(t('dataRepair.afterState'), stateRows)}
                    </>
                  );
                })()}
              </>
            ) : (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                  {t('dataRepair.afterState')}
                </h4>
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  {t('dataRepair.notSimulated')}
                </p>
              </div>
            )}
          </div>
        )}

        {lastApplyResult && (
          <div className="space-y-3 border-t border-gray-200 dark:border-gray-700 pt-4">
            <p className="text-sm font-medium text-green-700 dark:text-green-400">
              {t('dataRepair.applyResultTitle').replace('{{id}}', String(lastApplyResult.repairLogId))}
            </p>
            {renderStateTable(t('dataRepair.beforeState'), lastApplyResult.before)}
            {renderStateTable(t('dataRepair.afterState'), lastApplyResult.after)}
            {lastApplyResult.reverseSync && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('dataRepair.reverseSyncStamped').replace('{{count}}', String(lastApplyResult.reverseSync.rowsStamped ?? 0))}
              </p>
            )}
          </div>
        )}

        {selectedMeta?.reverseSync && (
          <p className="text-xs text-gray-500 dark:text-gray-400">{t('dataRepair.reverseSyncNote')}</p>
        )}

        {error && (
          <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-md px-3 py-2">{error}</div>
        )}
        {success && (
          <div className="text-sm text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 rounded-md px-3 py-2">{success}</div>
        )}

        {/* WCO-3277 review: without this an applied repair becomes un-rollbackable from the
            UI after a refresh, because the repairLogId lived only in page state. */}
        {history.length > 0 && (
          <div className="space-y-2 border-t border-gray-200 dark:border-gray-700 pt-4">
            <div className="flex items-center gap-1.5">
              <History className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{t('dataRepair.historyTitle')}</h4>
            </div>
            <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-md">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-2 py-1 text-left font-medium text-gray-600 dark:text-gray-400">{t('dataRepair.historyId')}</th>
                    <th className="px-2 py-1 text-left font-medium text-gray-600 dark:text-gray-400">{t('dataRepair.historyAction')}</th>
                    <th className="px-2 py-1 text-left font-medium text-gray-600 dark:text-gray-400">{t('dataRepair.historyOperation')}</th>
                    <th className="px-2 py-1 text-left font-medium text-gray-600 dark:text-gray-400">{t('dataRepair.historyActor')}</th>
                    <th className="px-2 py-1 text-left font-medium text-gray-600 dark:text-gray-400">{t('dataRepair.historyWhen')}</th>
                    <th className="px-2 py-1 text-left font-medium text-gray-600 dark:text-gray-400">{t('dataRepair.historyOutcome')}</th>
                    <th className="px-2 py-1" />
                  </tr>
                </thead>
                <tbody>
                  {history.map((entry) => {
                    const canRollback = entry.operation === 'apply' && !entry.rolled_back && !entry.error_message;
                    return (
                      <tr key={entry.id} className="border-t border-gray-100 dark:border-gray-700">
                        <td className="px-2 py-1 font-mono text-gray-700 dark:text-gray-300">{entry.id}</td>
                        <td className="px-2 py-1 text-gray-700 dark:text-gray-300">{entry.action_id}</td>
                        <td className="px-2 py-1 text-gray-700 dark:text-gray-300">{entry.operation}</td>
                        <td className="px-2 py-1 text-gray-700 dark:text-gray-300">{entry.actor_name || entry.actor_id || '—'}</td>
                        <td className="px-2 py-1 text-gray-500 dark:text-gray-400">{new Date(entry.performed_at).toLocaleString()}</td>
                        <td className="px-2 py-1">
                          {entry.error_message ? (
                            <span className="text-red-600 dark:text-red-400" title={entry.error_message}>
                              {t('dataRepair.historyFailed')}
                            </span>
                          ) : entry.rolled_back ? (
                            <span className="text-gray-500 dark:text-gray-400">{t('dataRepair.historyRolledBack')}</span>
                          ) : (
                            <span className="text-green-700 dark:text-green-400">
                              {t('dataRepair.historyRows').replace('{{n}}', String(entry.row_count))}
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-1 text-right">
                          {canRollback && (
                            <button
                              onClick={() => rollbackById(entry.action_id, entry.id)}
                              disabled={loading !== null}
                              className="text-primary-600 dark:text-primary-400 underline disabled:opacity-50"
                            >
                              {t('dataRepair.rollbackButton')}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {showApplyConfirm && preview && previewInput && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6 space-y-4">
            <div className="flex items-center gap-3 text-amber-600 dark:text-amber-400">
              <AlertTriangle className="h-6 w-6" />
              <h3 className="text-lg font-semibold">{t('dataRepair.applyConfirmTitle')}</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300">{t('dataRepair.applyConfirmBody')}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('dataRepair.applyRecheckNote')}</p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowApplyConfirm(false)}
                className="px-4 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600"
              >
                {t('dataRepair.cancel')}
              </button>
              <button
                onClick={handleApply}
                disabled={loading === 'apply'}
                className="px-4 py-2 text-sm rounded-md bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50"
              >
                {loading === 'apply' ? t('dataRepair.applyLoading') : t('dataRepair.applyConfirmButton')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataRepairPanel;
